import dayjs from 'dayjs'
import { AuthStorageInstance } from 'services/Storage/AuthStorage'
import { OrderItem } from 'state/new-summary/orders/orders.types'

/*
 *   Takes a string and generates initials with first letter of first two words in string.
 *   If empty string is passed it returns 'OK'
 *   @arg: string
 *   @return: string
 *   Used in Avatar
 */
export const generateInitials = (str: string = '') => {
  const splitName = str
    .split(' ')
    .map((word) => word.split('')[0])
    .join('')
    .toUpperCase()
  if (splitName.length === 0) {
    return 'OK'
  }
  if (splitName.length < 2) {
    return splitName[0]
  }
  return `${splitName[0]}${splitName[1]}`
}

export const getIsAdmin = () => {
  let isAdmin: boolean
  try {
    isAdmin = AuthStorageInstance.getIsAdmin()
  } catch {
    isAdmin = true
  }
  return isAdmin
}

export const getDefaultCollectionListName = (
  listNameProp: { name: string }[],
  key: 'Collection' | 'Supply'
) => {
  const collectionListNames = listNameProp?.map((itm) => itm?.name?.toLocaleLowerCase())
  let listLength = listNameProp.length
  let listName = `${key} list ${listLength}`

  while (collectionListNames?.includes(listName)) {
    listName = `${key} list ${listLength}`
    listLength += 1
  }
  return listName
}

export const getInvoiceAge = (dateStamp: string | number) => {
  const invoiceDate = dayjs.unix(Number(dateStamp))
  const currentDate = dayjs()
  const difference = currentDate.diff(invoiceDate, 'day')
  return difference ? `${difference} days` : 'Today'
}

export const matchCaseInsensitive = (inputString: string, searchString: string) => {
  let result = ''
  for (const char of inputString) {
    if (searchString.toLowerCase().includes(char.toLowerCase())) {
      result += searchString[searchString.toLowerCase().indexOf(char.toLowerCase())]
    } else {
      result += char
    }
  }
  return result
}

export const calculateFactorAndRemainder = ({
  quantity,
  factor = 0
}: {
  quantity: number
  factor: number
}) => {
  const largeUnitValue = factor < 1 ? 0 : Math.floor(quantity / factor) // Calculate full factors
  const smallUnitValue = factor < 1 ? quantity : quantity % factor // Calculate remainder quantity
  return {
    largeUnitValue,
    smallUnitValue
  }
}
export const getItemUnit = (reqItem: OrderItem['addedItemsItem']) => {
  const { largeUnitValue, smallUnitValue } = calculateFactorAndRemainder({
    quantity: reqItem.quantity,
    factor: reqItem?.secondaryUnits?.[0].factor || 0
  })
  const largeUnit =
    largeUnitValue > 0 ? `${largeUnitValue} ${reqItem?.secondaryUnits?.[0].name}` : ''
  const smallUnit = smallUnitValue > 0 ? `${smallUnitValue} ${reqItem?.primaryUnit || 'units'}` : ''
  const separator = largeUnit && smallUnit ? ', ' : ''
  return `${largeUnit}${separator}${smallUnit}`
}

export const calculateTotalWeight = (reqItemProp: OrderItem['addedItemsItem']) => {
  let summedWeightInKgs = 0
  for (const reqItem of [reqItemProp]) {
    const factor = reqItem.secondaryUnits?.[0]?.factor

    let primaryUnitQuantity = 0
    let secondaryUnitQuantity = 0

    if (!factor) {
      primaryUnitQuantity = reqItem.quantity
      secondaryUnitQuantity = 0
    } else {
      primaryUnitQuantity = reqItem.quantity % factor
      secondaryUnitQuantity = Math.floor(reqItem.quantity / factor)
    }

    let primaryUnitWeight = 0
    let secondaryUnitWeight = 0

    if (primaryUnitQuantity) {
      let unitWeightInKg = 0
      if (reqItem.qtt_props?.weight) {
        const unit = reqItem.qtt_props.weight.unit?.toLowerCase()
        if (unit === 'g' || unit === 'gram') {
          unitWeightInKg = reqItem.qtt_props.weight.val / 1000 // grams to kilograms
        } else if (unit === 'mg' || unit === 'milligram') {
          unitWeightInKg = reqItem.qtt_props.weight.val / 1000000 // milligrams to kilograms
        } else if (unit === 't' || unit === 'tonne' || unit === 'ton') {
          unitWeightInKg = reqItem.qtt_props.weight.val * 1000 // tonnes to kilograms
        } else if (unit === 'q' || unit === 'quintal') {
          unitWeightInKg = reqItem.qtt_props.weight.val * 100 // quintal to kilograms
        } else if (unit === 'kg' || unit === 'kilogram') {
          unitWeightInKg = reqItem.qtt_props.weight.val
        }

        const weightInKg = unitWeightInKg * primaryUnitQuantity
        primaryUnitWeight = weightInKg
      }
    }

    if (secondaryUnitQuantity) {
      let secondaryUnitWeightInKg = 0
      if (reqItem.secondaryUnits?.[0]?.qtt_props?.weight) {
        const unit = reqItem.secondaryUnits?.[0]?.qtt_props.weight?.unit?.toLowerCase()
        if (unit === 'g' || unit === 'gram') {
          secondaryUnitWeightInKg = reqItem.secondaryUnits?.[0]?.qtt_props.weight.val / 1000 // grams to kilograms
        } else if (unit === 'mg' || unit === 'milligram') {
          secondaryUnitWeightInKg = reqItem.secondaryUnits?.[0]?.qtt_props.weight.val / 1000000 // milligrams to kilograms
        } else if (unit === 't' || unit === 'tonne' || unit === 'ton') {
          secondaryUnitWeightInKg = reqItem.secondaryUnits?.[0]?.qtt_props.weight.val * 1000 // tonnes to kilograms
        } else if (unit === 'q' || unit === 'quintal') {
          secondaryUnitWeightInKg = reqItem.secondaryUnits?.[0]?.qtt_props.weight.val * 100 // quintal to kilograms
        } else if (unit === 'kg' || unit === 'kilogram') {
          secondaryUnitWeightInKg = reqItem.secondaryUnits?.[0]?.qtt_props.weight.val
        }

        const weightInKg = secondaryUnitWeightInKg * secondaryUnitQuantity
        secondaryUnitWeight = weightInKg
      }
    }
    summedWeightInKgs += primaryUnitWeight + secondaryUnitWeight
  }
  if (summedWeightInKgs <= 0) {
    return ''
  }
  return `(${summedWeightInKgs} ${summedWeightInKgs > 1 ? 'Kgs' : 'Kg'})`
}

export const calculateSummedTotalWeight = (reqItems: OrderItem['addedItemsItem'][]) => {
  let summedWeightInKgs = 0
  for (const reqItem of reqItems) {
    const factor = reqItem.secondaryUnits?.[0]?.factor

    let primaryUnitQuantity = 0
    let secondaryUnitQuantity = 0

    if (!factor) {
      primaryUnitQuantity = reqItem.quantity
      secondaryUnitQuantity = 0
    } else {
      primaryUnitQuantity = reqItem.quantity % factor
      secondaryUnitQuantity = Math.floor(reqItem.quantity / factor)
    }

    let primaryUnitWeight = 0
    let secondaryUnitWeight = 0

    if (primaryUnitQuantity) {
      let unitWeightInKg = 0
      if (reqItem.qtt_props?.weight) {
        const unit = reqItem.qtt_props.weight.unit?.toLowerCase()
        if (unit === 'g' || unit === 'gram') {
          unitWeightInKg = reqItem.qtt_props.weight.val / 1000 // grams to kilograms
        } else if (unit === 'mg' || unit === 'milligram') {
          unitWeightInKg = reqItem.qtt_props.weight.val / 1000000 // milligrams to kilograms
        } else if (unit === 't' || unit === 'tonne' || unit === 'ton') {
          unitWeightInKg = reqItem.qtt_props.weight.val * 1000 // tonnes to kilograms
        } else if (unit === 'q' || unit === 'quintal') {
          unitWeightInKg = reqItem.qtt_props.weight.val * 100 // quintal to kilograms
        } else if (unit === 'kg' || unit === 'kilogram') {
          unitWeightInKg = reqItem.qtt_props.weight.val
        }

        const weightInKg = unitWeightInKg * primaryUnitQuantity
        primaryUnitWeight = weightInKg
      }
    }

    if (secondaryUnitQuantity) {
      let secondaryUnitWeightInKg = 0
      if (reqItem.secondaryUnits?.[0]?.qtt_props?.weight) {
        const unit = reqItem.secondaryUnits?.[0]?.qtt_props.weight?.unit?.toLowerCase()
        if (unit === 'g' || unit === 'gram') {
          secondaryUnitWeightInKg = reqItem.secondaryUnits?.[0]?.qtt_props.weight.val / 1000 // grams to kilograms
        } else if (unit === 'mg' || unit === 'milligram') {
          secondaryUnitWeightInKg = reqItem.secondaryUnits?.[0]?.qtt_props.weight.val / 1000000 // milligrams to kilograms
        } else if (unit === 't' || unit === 'tonne' || unit === 'ton') {
          secondaryUnitWeightInKg = reqItem.secondaryUnits?.[0]?.qtt_props.weight.val * 1000 // tonnes to kilograms
        } else if (unit === 'q' || unit === 'quintal') {
          secondaryUnitWeightInKg = reqItem.secondaryUnits?.[0]?.qtt_props.weight.val * 100 // quintal to kilograms
        } else if (unit === 'kg' || unit === 'kilogram') {
          secondaryUnitWeightInKg = reqItem.secondaryUnits?.[0]?.qtt_props.weight.val
        }

        const weightInKg = secondaryUnitWeightInKg * secondaryUnitQuantity
        secondaryUnitWeight = weightInKg
      }
    }
    summedWeightInKgs += primaryUnitWeight + secondaryUnitWeight
  }
  if (summedWeightInKgs <= 0) {
    return ''
  }
  return `(${summedWeightInKgs} ${summedWeightInKgs > 1 ? 'Kgs' : 'Kg'})`
}
